import { useCallback, useEffect, useRef, useState } from 'react'
import { exportWorkbook } from '@utils/excel/ExcelExport'
import { CLOUD_API } from '@core/pages/admin/shared'
import { useAuthToken } from '@core/pages/admin/useAuthToken'
import { httpErrorText } from '@core/utils/httpError'
import { btn, btnBase, btnSize, cx, hint, table, tableWrap, td, th } from '@ui/classes'
import { errBanner, filterBar, filterChip, filterChipActive, okBanner } from '@ui/admin'

type Verdict = 'valid' | 'invalid' | 'unknown'
type Filter = Verdict | 'all'
type Period = 'today' | 'week' | 'month'

/** Строка журнала `cz_checks` из `GET /cz/checks`. */
interface CheckRow {
  id: string
  receipt_id: string | null
  pg: string
  code: string
  status: string | null
  error_message: string | null
  checked_at: string
  /** `scan` — проверка на кассе, `reconcile` — пост-фактум-сверка. */
  source: 'scan' | 'reconcile'
  verdict: Verdict
}

interface ChecksResponse {
  count: number
  /** Всего за период (без фильтра) — для счётчиков. */
  total: number
  /** Всего под текущим фильтром — по нему листаем. */
  filtered_total: number
  limit: number
  offset: number
  summary: Record<Verdict, number>
  checks: CheckRow[]
}

/** Метрики ЧЗ из `GET /cz/metrics` (65.12.9). */
interface CzMetrics {
  hours: number
  checks: {
    total: number
    valid: number
    invalid: number
    unknown: number
    scan: number
    reconcile: number
  }
  unknown_share: number
  backlog: { receipts: number; due: number }
}

/** Отчёт `POST /cz/reconcile` (см. backend/src/cz/reconcile.ts). */
interface ReconcileReport {
  receipts: number
  codes: number
  updated: Record<Verdict, number>
  released: number
}

const VERDICT_LABEL: Record<Filter, string> = {
  all: 'Все',
  valid: 'Подтверждённые',
  invalid: 'Не подтверждённые',
  unknown: 'Не проверены'
}

const VERDICT_CLASS: Record<Verdict, string> = {
  valid: 'rounded-md border border-ok px-1.5 py-0.5 text-tiny font-semibold text-okDark',
  invalid:
    'rounded-md border border-warnLine bg-red-50 px-1.5 py-0.5 text-tiny font-semibold text-dangerDark',
  unknown: 'rounded-md bg-soft px-1.5 py-0.5 text-tiny text-muted'
}

const PERIOD_LABEL: Record<Period, string> = {
  today: 'Сегодня',
  week: '7 дней',
  month: '30 дней'
}

const PAGE_SIZE = 200
const EXPORT_LIMIT = 5000

/** Начало периода в ISO (локальные сутки). */
function periodStart(period: Period): string {
  const from = new Date()
  from.setHours(0, 0, 0, 0)
  if (period === 'week') from.setDate(from.getDate() - 6)
  if (period === 'month') from.setDate(from.getDate() - 29)
  return from.toISOString()
}

/** Время проверки в локальном формате. */
function timeText(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU')
}

/**
 * «Журнал проверок» внутри карточки ЧЗ (ARCH-TASK-65.12.8).
 *
 * Рыночный аналог «рабочего места проверки кодов»: список проверок с вердиктом
 * и причиной, сводка по статусам, ручной запуск сверки и выгрузка в Excel.
 */
export function CzJournalSection() {
  const token = useAuthToken()
  const [filter, setFilter] = useState<Filter>('all')
  const [period, setPeriod] = useState<Period>('week')
  const [data, setData] = useState<ChecksResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  /** 403 — журнал только для директора: не показываем красную ошибку кассиру. */
  const [forbidden, setForbidden] = useState(false)
  /** Номер последнего запроса: ответы старого фильтра не должны перебивать. */
  const requestRef = useRef(0)
  const [metrics, setMetrics] = useState<CzMetrics | null>(null)

  const buildQuery = useCallback(
    (limit: number, offset: number): string => {
      const params = new URLSearchParams({
        from: periodStart(period),
        to: new Date().toISOString(),
        limit: String(limit),
        offset: String(offset)
      })
      if (filter !== 'all') params.set('status', filter)
      return params.toString()
    },
    [filter, period]
  )

  const load = useCallback(
    async (offset = 0) => {
      if (!token) return
      const requestId = ++requestRef.current
      setBusy(true)
      setError(null)
      try {
        const response = await fetch(`${CLOUD_API}/cz/checks?${buildQuery(PAGE_SIZE, offset)}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (response.status === 403) {
          if (requestId === requestRef.current) setForbidden(true)
          return
        }
        if (!response.ok) {
          if (requestId === requestRef.current) {
            setError(httpErrorText('Журнал проверок', response.status))
          }
          return
        }
        const next = (await response.json()) as ChecksResponse
        // Ответ устаревшего фильтра игнорируем: иначе он перетрёт новый.
        if (requestId !== requestRef.current) return
        setForbidden(false)
        // «Показать ещё» дописывает страницу, смена фильтра — заменяет.
        setData((prev) =>
          offset > 0 && prev ? { ...next, checks: [...prev.checks, ...next.checks] } : next
        )
      } catch {
        if (requestId === requestRef.current) {
          setError('Нет связи с облаком — журнал недоступен')
        }
      } finally {
        if (requestId === requestRef.current) setBusy(false)
      }
    },
    [token, buildQuery]
  )

  useEffect(() => {
    void load(0)
  }, [load])

  /** Метрики за 24 ч: проверки, доля unknown и backlog (65.12.9). */
  const loadMetrics = useCallback(async () => {
    if (!token) return
    try {
      const response = await fetch(`${CLOUD_API}/cz/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) return
      setMetrics((await response.json()) as CzMetrics)
    } catch {
      // Метрики не критичны: журнал работает и без них.
    }
  }, [token])

  useEffect(() => {
    void loadMetrics()
  }, [loadMetrics])

  /** Ручная сверка чеков с контуром (cron появится в 65.12.9). */
  const reconcile = async () => {
    if (!token) return
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      const response = await fetch(`${CLOUD_API}/cz/reconcile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ limit: 100 })
      })
      if (!response.ok) {
        setError(httpErrorText('Сверка с Честным Знаком', response.status))
        return
      }
      const report = (await response.json()) as ReconcileReport
      setNote(
        `Сверка: чеков ${report.receipts}, кодов ${report.codes}; ` +
          `подтверждено ${report.updated.valid}, не подтверждено ${report.updated.invalid}, ` +
          `не проверено ${report.updated.unknown}, отложено ${report.released}`
      )
      await load(0)
      void loadMetrics()
    } catch {
      setError('Не удалось выполнить сверку: нет связи с облаком')
    } finally {
      setBusy(false)
    }
  }

  /** Выгрузка текущего фильтра в Excel (до 5000 строк). */
  const exportToExcel = async () => {
    if (!token) return
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      const response = await fetch(`${CLOUD_API}/cz/checks?${buildQuery(EXPORT_LIMIT, 0)}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) {
        setError(httpErrorText('Выгрузка журнала', response.status))
        return
      }
      const full = (await response.json()) as ChecksResponse
      const stamp = new Date().toISOString().slice(0, 10)
      const result = await exportWorkbook(`cz_checks_${stamp}.xlsx`, [
        {
          name: 'Проверки',
          columns: [
            { title: 'Проверено', width: 20 },
            { title: 'Код', width: 34 },
            { title: 'Вердикт', width: 12 },
            { title: 'Источник', width: 10 },
            { title: 'Статус ЧЗ', width: 16 },
            { title: 'Группа', width: 12 },
            { title: 'Причина', width: 40 },
            { title: 'Чек', width: 38 }
          ],
          rows: full.checks.map((row) => [
            timeText(row.checked_at),
            row.code,
            row.verdict,
            row.source === 'scan' ? 'Скан' : 'Сверка',
            row.status ?? '',
            row.pg,
            row.error_message ?? '',
            row.receipt_id ?? ''
          ])
        }
      ])
      setNote(
        result === 'saved-to-documents'
          ? `Файл сохранён в «Документах» (${full.checks.length} строк)`
          : `Файл скачан (${full.checks.length} строк)`
      )
      if (full.total > full.checks.length) {
        setNote((prev) => `${prev ?? ''} · выгружены первые ${full.checks.length} из ${full.total}`)
      }
    } catch {
      setError('Не удалось выгрузить журнал')
    } finally {
      setBusy(false)
    }
  }

  if (!token) {
    return (
      <div className="mt-4 border-t border-line pt-3">
        <h4 className="mb-1 mt-0 text-base2 font-bold">Журнал проверок</h4>
        <p className={hint}>Подключите облако, чтобы видеть проверки кодов в ЧЗ.</p>
      </div>
    )
  }

  if (forbidden) {
    return (
      <div className="mt-4 border-t border-line pt-3">
        <h4 className="mb-1 mt-0 text-base2 font-bold">Журнал проверок</h4>
        <p className={hint}>Журнал проверок доступен директору (роль admin).</p>
      </div>
    )
  }


  return (
    <div className="mt-4 border-t border-line pt-3">
      <h4 className="mb-1 mt-0 text-base2 font-bold">Журнал проверок</h4>
      <p className={hint}>
        Каждая проверка кода в контуре: вердикт, статус ЧЗ и причина. Выгрузка — до{' '}
        {EXPORT_LIMIT} строк за период.
      </p>

      {metrics && (
        <p className={hint}>
          За {metrics.hours} ч: проверок {metrics.checks.total} · подтверждено {metrics.checks.valid} ·
          не подтверждено {metrics.checks.invalid} · не проверено {metrics.checks.unknown} · без статуса{' '}
          {metrics.backlog.receipts} (к сверке {metrics.backlog.due})
        </p>
      )}
      {metrics && metrics.checks.total >= 20 && metrics.unknown_share >= 0.5 && (
        <p className={errBanner}>
          Высокая доля unknown ({Math.round(metrics.unknown_share * 100)}%) — проверьте связь
          с Честным Знаком.
        </p>
      )}

      <div className={filterBar}>
        {(['all', 'valid', 'invalid', 'unknown'] as Filter[]).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setFilter(value)}
            className={cx(filter === value ? filterChipActive : filterChip)}
          >
            {VERDICT_LABEL[value]}
            {data && value !== 'all' ? ` · ${data.summary[value]}` : ''}
          </button>
        ))}
        <span className="mx-1 h-4 w-px bg-line" />
        {(Object.keys(PERIOD_LABEL) as Period[]).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setPeriod(value)}
            className={cx(period === value ? filterChipActive : filterChip)}
          >
            {PERIOD_LABEL[value]}
          </button>
        ))}
      </div>

      {error && <p className={errBanner}>{error}</p>}
      {note && <p className={okBanner}>{note}</p>}

      <div className="mb-2 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void load(0)}
          disabled={busy}
          className={cx(btnBase, btn.secondary, btnSize.sm)}
        >
          {busy ? 'Загрузка…' : 'Обновить'}
        </button>
        <button
          type="button"
          onClick={() => void reconcile()}
          disabled={busy}
          className={cx(btnBase, btn.secondary, btnSize.sm)}
          title="Проверить чеки без статуса ЧЗ (до 100 за запуск)"
        >
          Сверить сейчас
        </button>
        <button
          type="button"
          onClick={() => void exportToExcel()}
          disabled={busy || !data || data.checks.length === 0}
          className={cx(btnBase, btn.secondary, btnSize.sm)}
        >
          Выгрузить в Excel
        </button>
      </div>

      {!data || data.checks.length === 0 ? (
        <p className={hint}>{busy ? 'Читаем журнал…' : 'За период проверок нет.'}</p>
      ) : (
        <>
          <div className={tableWrap}>
            <table className={table}>
              <thead>
                <tr>
                  <th className={th}>Проверено</th>
                  <th className={th}>Код</th>
                  <th className={th}>Вердикт</th>
                  <th className={th}>Источник</th>
                  <th className={th}>Статус ЧЗ</th>
                  <th className={th}>Причина</th>
                </tr>
              </thead>
              <tbody>
                {data.checks.map((row) => (
                  <tr key={row.id}>
                    <td className={td}>{timeText(row.checked_at)}</td>
                    <td className={cx(td, 'font-mono text-tiny')} title={row.code}>
                      {row.code.length > 28 ? `${row.code.slice(0, 28)}…` : row.code}
                      <span className="mt-0.5 block font-sans text-tiny text-muted">{row.pg}</span>
                      {row.receipt_id && (
                        <span className="block font-sans text-tiny text-muted">
                          чек {row.receipt_id.slice(0, 8)}…
                        </span>
                      )}
                    </td>
                    <td className={td}>
                      <span className={VERDICT_CLASS[row.verdict]}>
                        {VERDICT_LABEL[row.verdict]}
                      </span>
                    </td>
                    <td className={cx(td, 'whitespace-nowrap text-small text-muted')}>
                      {row.source === 'scan' ? 'Скан' : 'Сверка'}
                    </td>
                    <td className={td}>{row.status ?? '—'}</td>
                    <td className={td}>{row.error_message ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.checks.length < data.filtered_total && (
            <button
              type="button"
              onClick={() => void load(data.checks.length)}
              disabled={busy}
              className={cx(btnBase, btn.secondary, btnSize.sm, 'mt-2')}
            >
              Показать ещё
            </button>
          )}
        </>
      )}
    </div>
  )
}

